import { useState } from 'react';
import { useMapContext } from '../../map/MapContext';

const VERTEX_NOUN: Record<string, string> = {
  point: 'точку',
  multipoint: 'точку',
  line: 'вершину линии',
  multiline: 'вершину линии',
  polygon: 'вершину полигона',
  multipolygon: 'вершину полигона',
};

function ModeToggle({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`flex h-8 shrink-0 items-center rounded-[9px] px-3 text-[12px] font-medium ${
        active
          ? 'bg-action-primary text-text-inverse'
          : 'border border-border-default text-text-primary hover:bg-bg-surface-secondary'
      }`}
    >
      {label}
    </button>
  );
}

export function DrawModeBanner() {
  const {
    drawActive,
    drawLayerId,
    drawError,
    layers,
    addObjectActive,
    startAddObject,
    stopAddObject,
    addObjectAtCoordinate,
    boxSelectActive,
    startBoxSelect,
    stopDraw,
    stopBoxSelect,
    selectionResults,
    deleteSelectionResults,
    clearSelectionResults,
  } = useMapContext();
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [lonInput, setLonInput] = useState('');
  const [latInput, setLatInput] = useState('');
  const [locating, setLocating] = useState(false);
  const [locateError, setLocateError] = useState<string | null>(null);

  if (!drawActive) return null;

  const cfg = layers.find((l) => l.id === drawLayerId);
  const layerName = cfg?.name ?? 'слой';
  const vertexNoun = VERTEX_NOUN[cfg?.geometryType ?? ''] ?? 'точку';
  const selectedCount = selectionResults.length;

  const handleFinish = () => {
    stopDraw();
    stopBoxSelect();
    clearSelectionResults();
  };

  const handleDeleteSelected = async () => {
    if (deleting) return; // защита от повторного клика, пока запрос ещё в пути
    if (!window.confirm(`Удалить выбранные объекты (${selectedCount})? Это действие нельзя отменить.`)) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      const { deleted, failed } = await deleteSelectionResults();
      if (failed > 0) {
        setDeleteError(
          deleted > 0
            ? `Удалено ${deleted} из ${deleted + failed} — часть объектов не удалось удалить (нет доступа или уже удалены).`
            : 'Не удалось удалить объекты — нет доступа или они уже удалены.',
        );
      }
    } finally {
      setDeleting(false);
    }
  };

  const handleUseGeolocation = () => {
    if (!navigator.geolocation) {
      setLocateError('Геолокация не поддерживается этим браузером');
      return;
    }
    setLocating(true);
    setLocateError(null);
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLonInput(String(pos.coords.longitude));
        setLatInput(String(pos.coords.latitude));
        setLocating(false);
      },
      (err) => {
        setLocating(false);
        let message: string;
        if (err.code === err.PERMISSION_DENIED) {
          message =
            'Геолокация недоступна, хотя разрешение в браузере есть — скорее всего, её блокирует Windows. ' +
            'Откройте Параметры Windows → Конфиденциальность и защита → Расположение, включите службу геолокации ' +
            'и разрешите доступ для вашего браузера, затем обновите страницу.';
        } else if (err.code === err.POSITION_UNAVAILABLE) {
          message = 'Не удалось определить координаты — устройство не смогло получить сигнал местоположения';
        } else if (err.code === err.TIMEOUT) {
          message = 'Определение местоположения заняло слишком много времени — попробуйте ещё раз';
        } else {
          message = 'Не удалось определить местоположение';
        }
        setLocateError(message);
      },
      { timeout: 15000, enableHighAccuracy: true, maximumAge: 60000 },
    );
  };

  const handleAddAtCoordinate = () => {
    const lon = parseFloat(lonInput.replace(',', '.'));
    const lat = parseFloat(latInput.replace(',', '.'));
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;
    addObjectAtCoordinate(lon, lat);
    setLonInput('');
    setLatInput('');
  };

  const hint = drawError
    ? null
    : deleteError
      ? null
      : addObjectActive
        ? `Клик — добавить ${vertexNoun}`
        : boxSelectActive
          ? 'Рамка (зажать и потянуть) — выделить объекты для массового удаления'
          : 'Клик по объекту — выбрать. Включите режим ниже для добавления или массовых действий';

  return (
    <div className="absolute left-1/2 top-6 z-20 flex w-[min(640px,calc(100vw-32px))] -translate-x-1/2 flex-col gap-2 rounded-2xl border border-border-default bg-bg-surface px-4 py-2.5 shadow-lg">
      <div className="flex flex-wrap items-center gap-3">
        <span className="size-2.5 shrink-0 animate-pulse rounded-full bg-action-primary" />
        <div className="flex flex-col">
          <span className="text-[13px] font-medium text-text-primary">Редактирование объектов — {layerName}</span>
          {drawError ? (
            <span className="text-[11px] font-medium text-status-danger">{drawError}</span>
          ) : deleteError ? (
            <span className="text-[11px] font-medium text-status-danger">{deleteError}</span>
          ) : (
            <span className="text-[11px] text-text-secondary">{hint}</span>
          )}
        </div>

        <ModeToggle
          label="Добавить объект"
          active={addObjectActive}
          onClick={() => (addObjectActive ? stopAddObject() : startAddObject())}
        />
        <ModeToggle
          label="Выбор рамкой"
          active={boxSelectActive}
          onClick={() => (boxSelectActive ? stopBoxSelect() : startBoxSelect('active'))}
        />

        {selectedCount > 0 && (
          <button
            type="button"
            onClick={handleDeleteSelected}
            disabled={deleting}
            className="flex h-8 shrink-0 items-center gap-1.5 rounded-[9px] bg-status-danger px-3 text-[12px] font-medium text-text-inverse hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60"
          >
            {deleting && (
              <span className="size-3 shrink-0 animate-spin rounded-full border-2 border-text-inverse/40 border-t-text-inverse" />
            )}
            {deleting ? 'Удаляем…' : `Удалить выбранное (${selectedCount})`}
          </button>
        )}
        <button
          type="button"
          onClick={handleFinish}
          disabled={deleting}
          className="flex h-8 shrink-0 items-center rounded-[9px] border border-border-default bg-bg-surface px-3 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-60"
        >
          Завершить
        </button>
      </div>

      {addObjectActive && (
        <div className="flex flex-col gap-1.5 border-t border-border-default pt-2">
          <div className="flex flex-wrap items-center gap-2">
            <span className="shrink-0 text-[11px] text-text-secondary">Добавить {vertexNoun} по координатам:</span>
            <input
              type="text"
              inputMode="decimal"
              placeholder="Долгота"
              value={lonInput}
              onChange={(e) => setLonInput(e.target.value)}
              className="h-7 w-24 rounded-md border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
            />
            <input
              type="text"
              inputMode="decimal"
              placeholder="Широта"
              value={latInput}
              onChange={(e) => setLatInput(e.target.value)}
              className="h-7 w-24 rounded-md border border-border-default bg-bg-surface px-2 text-[12px] text-text-primary outline-none"
            />
            <button
              type="button"
              onClick={handleUseGeolocation}
              disabled={locating}
              className="flex h-7 shrink-0 items-center rounded-md border border-border-default px-2.5 text-[12px] font-medium text-text-primary hover:bg-bg-surface-secondary disabled:cursor-not-allowed disabled:opacity-60"
            >
              {locating ? 'Определяем…' : 'Использовать геолокацию'}
            </button>
            <button
              type="button"
              onClick={handleAddAtCoordinate}
              disabled={!lonInput || !latInput}
              className="flex h-7 shrink-0 items-center rounded-md bg-action-primary px-2.5 text-[12px] font-medium text-text-inverse disabled:cursor-not-allowed disabled:opacity-50"
            >
              Добавить
            </button>
          </div>
          {locateError && <p className="text-[11px] font-medium text-status-danger">{locateError}</p>}
        </div>
      )}
    </div>
  );
}
